import { Grid, Divider, AutoCenter, Image, Popup, Empty } from 'antd-mobile';
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_ROUTES from '../../api';
import { HAPPY_EVENT_ID, isHappyEvent, isScoreEvent } from '../../features/match/model/gameEvents';
import Icon from '../../icon';
import './index.css'

const GameEventList = ({ gameInfo }) => {

    const navigate = useNavigate()
    const [selectedEventAlbum, setSelectedEventAlbum] = useState(null)
    const eventInfo = gameInfo?.gameEventInfoVOList || []
    const selectedEventMediaList = useMemo(() => selectedEventAlbum?.mediaList || [], [selectedEventAlbum])

    const getMediaUrl = (item) => item?.mediaUrl || item?.media_url || item?.url || '';

    const getMediaType = (item) => Number(item?.mediaType ?? item?.media_type);

    const getEventMediaList = (event) => event?.albumMediaList || event?.mediaList || event?.album_media_list || [];

    const getMediaDisplayName = (item) => item?.remark || item?.fileName || item?.file_name || (getMediaType(item) === 2 ? '视频记录' : '图片记录');
    const happyEvents = eventInfo.filter(item => isHappyEvent(item.eventId));
    const normalEventInfo = eventInfo.filter(item => String(item.eventId) !== HAPPY_EVENT_ID);
    const currentGameType = Number(gameInfo?.gameType ?? gameInfo?.game_type);
    const isExternalMatch = [1, 2].includes(currentGameType);
    const isKepengCupActivity = currentGameType === 4
        && `${gameInfo?.gameName || ''} ${gameInfo?.gameRemark || ''}`.includes('科朋杯');
    const isValidTeamImage = (url) => typeof url === 'string' && url.startsWith('https');
    const getImageWithFooter = (url) => isValidTeamImage(url) ? url + Icon.ImageFooter : '';
    const getMemberDefaultAvatar = (member) => {
        // 外战对手在后端作为临时队员记录，头像优先按队员默认头像规则读取。
        const memberDefaultImage = member?.memberImageList?.find(item => Number(item.type) === 0)?.image;
        if (memberDefaultImage) return memberDefaultImage + Icon.ImageFooter;
        if (isValidTeamImage(member?.image)) return member.image + Icon.ImageFooter;
        if (isValidTeamImage(member?.memberImage)) return member.memberImage + Icon.ImageFooter;
        return '';
    };

    const getActivityTeamName = (team) => team === 1
        ? gameInfo?.greenName || gameInfo?.greenTeamName || '科朋队'
        : gameInfo?.redName || gameInfo?.redTeamName || '对手';

    const isKepengTeam = (team) => {
        const teamName = getActivityTeamName(team);
        if (teamName.includes('科朋')) return true;
        if (getActivityTeamName(team === 1 ? 2 : 1).includes('科朋')) return false;
        // 年会比赛队名有时只写“A队/B队”，此时默认绿队是科朋队。
        return team === 1;
    };

    const getActivityTeamLogo = (team) => {
        // 年会活动赛中不使用队长头像字段，科朋队固定使用俱乐部队徽。
        if (isKepengTeam(team)) return Icon.KEPENG_LOGO;
        const rawLogo = team === 1 ? gameInfo?.greenTeamLogo : gameInfo?.redTeamLogo;
        const logoPath = typeof rawLogo === 'string' ? rawLogo.split('?')[0] : '';
        if (!logoPath || /\/kplogo\.png$/i.test(logoPath)) return '';
        return getImageWithFooter(rawLogo);
    };

    const isActivityTeamNameFallback = (team) => isKepengCupActivity && !getActivityTeamLogo(team);

    const getFirstTeamLogo = () => {
        if (isKepengCupActivity) return getActivityTeamLogo(1);
        // 正式对外比赛的第一个队伍固定展示科朋队徽，避免误用对手或临时队伍头像。
        if (isExternalMatch) {
            return Icon.KEPENG_LOGO;
        }
        return gameInfo?.greenImage && currentGameType !== 3 ? gameInfo.greenImage + Icon.ImageFooter : Icon.KEPENG_LOGO;
    };

    const getSecondTeamImage = () => {
        if (isKepengCupActivity) return getActivityTeamLogo(2);
        if (!isExternalMatch) {
            return gameInfo?.redImage && currentGameType !== 3 ? gameInfo.redImage + Icon.ImageFooter : Icon.KEPENG_LOGO;
        }
        // 列表接口会批量返回临时对手队员头像，优先使用这个字段，避免 redCaptain 只有姓名时取不到图。
        return getImageWithFooter(gameInfo?.redCaptainImage) || getMemberDefaultAvatar(gameInfo?.redCaptain) || getImageWithFooter(gameInfo?.redImage) || getImageWithFooter(gameInfo?.redTeamLogo);
    };

    const getSecondTeamName = () => gameInfo?.redName || gameInfo?.redTeamName || '对手';

    const renderTeamLogo = (team) => {
        const logo = team === 1 ? getFirstTeamLogo() : getSecondTeamImage();
        const isNameFallback = isActivityTeamNameFallback(team);
        const isSecondTeamFallback = team === 2 && isExternalMatch && !logo;
        if (isNameFallback) {
            return (
                <div className="clean-team-logo clean-team-logo--fallback clean-team-logo--name-fallback" style={{ border: `3px solid ${team === 1 ? (gameInfo?.greenColor || '#00B42A') : (gameInfo?.redColor || '#F53F3F')}` }}>
                    <span className="clean-team-logo-name">{getActivityTeamName(team)}</span>
                </div>
            );
        }
        if (isSecondTeamFallback) {
            return (
                <div className="clean-team-logo clean-team-logo--fallback" style={{ border: `3px solid ${gameInfo?.redColor || '#F53F3F'}` }}>
                    <span className="clean-team-logo-ball">⚽</span>
                    <span className="clean-team-logo-name">{getSecondTeamName()}</span>
                </div>
            );
        }
        return (
            <Image
                src={logo}
                width={54}
                height={54}
                fit='cover'
                className="clean-team-logo"
                style={{ border: `3px solid ${team === 1 ? (gameInfo?.greenColor || '#00B42A') : (gameInfo?.redColor || '#F53F3F')}` }}
            />
        );
    };

    const recordMediaViews = (mediaList = []) => {
        // 首页和赛事历史的赛况影像被打开时，统一给本次展示的媒体增加浏览次数。
        const mediaIds = [...new Set(mediaList.map(media => media?.id).filter(Boolean))];
        if (!mediaIds.length) return;
        Promise.all(mediaIds.map(mediaId => (
            axios.post(API_ROUTES.album_view, null, { params: { mediaId } }).catch(() => null)
        )));
    };

    const buildThumbUrl = (item) => {
        const url = getMediaUrl(item);
        if (!url) return '';
        // 首页事件节点只加载缩略图，视频使用 OSS 截帧，图片使用项目内缩略图参数。
        if (getMediaType(item) === 2) {
            return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_10000,m_fast`;
        }
        return url + Icon.ImageFooter;
    };

    if (eventInfo.length === 0) return null;

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        return `${minutes}'`;
    }

    const parseEventDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        const raw = String(value).trim();
        // 兼容后端返回的空格时间和 ISO 时间，避免不同浏览器解析结果不一致。
        const normalized = raw.includes('T') ? raw : raw.replace(/-/g, '/');
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getPauseSecondsBetween = (startDate, endDate) => {
        if (!startDate || !endDate || endDate <= startDate) return 0;
        const pauseEvents = normalEventInfo
            .filter(item => String(item.eventId) === '18')
            .map(item => parseEventDate(item.currentTime))
            .filter(Boolean)
            .sort((a, b) => a - b);
        let pauseSeconds = 0;
        for (let index = 0; index < pauseEvents.length; index += 2) {
            const pauseStart = new Date(Math.max(startDate.getTime(), pauseEvents[index].getTime()));
            const pauseEnd = new Date(Math.min(endDate.getTime(), (pauseEvents[index + 1] || endDate).getTime()));
            if (pauseEnd > pauseStart) {
                pauseSeconds += Math.floor((pauseEnd - pauseStart) / 1000);
            }
        }
        return pauseSeconds;
    };

    const renderEventItem = (item) => {
        const team = item.gameTeam;
        const eventIdStr = String(item.eventId);
        const eventMediaList = getEventMediaList(item);
        // 首页事件节点优先展示视频缩略图，和比赛详情页的赛况展示保持一致。
        const eventCoverMedia = eventMediaList.find(media => getMediaType(media) === 2) || eventMediaList[0];
        const hasEventVideo = eventMediaList.some(media => getMediaType(media) === 2);
        const iconSrc = eventIdStr === "5" ? Icon.YELLOW_CARD :
            eventIdStr === "6" ? Icon.RED_CARD :
                ["12", "21"].includes(eventIdStr) ? Icon.PENALTY_KICK :
                    eventIdStr === "4" ? Icon.OWN_GOAL :
                        eventIdStr === "9" ? Icon.PENALTY_NOT_SCORED :
                            Icon.GOAL;

        return (
            <Grid columns={24} className="game-event-timeline-row">
                <Grid.Item span={10}>
                    {team === 1 ? (
                        <div className={`event-row-inner left ${eventMediaList.length > 0 ? 'has-media' : ''}`}>
                            <span className="compact-name-text">{item.userName}</span>
                            <span className="compact-time-text">{formatTime(item.eventTime)}</span>
                            <span className="compact-event-icon-wrap">
                                <img src={iconSrc} alt="icon" className="compact-event-icon" />
                            </span>
                        </div>
                    ) : null}
                </Grid.Item>
                <Grid.Item span={4} className="center-divider-col">
                    {eventMediaList.length > 0 && (
                        <button
                            type="button"
                            className="home-event-album-entry"
                            onClick={(event) => {
                                event.stopPropagation();
                                recordMediaViews(eventMediaList);
                                setSelectedEventAlbum({ event: item, mediaList: eventMediaList });
                            }}
                        >
                            <span className="home-event-album-thumb">
                                <img src={buildThumbUrl(eventCoverMedia)} alt={hasEventVideo ? '赛况视频缩略图' : '赛况图片缩略图'} />
                                {hasEventVideo && <i>▶</i>}
                            </span>
                            <span className={`home-event-album-chip ${hasEventVideo ? 'video' : 'image'}`}>
                                {hasEventVideo ? '视频' : '图片'} {eventMediaList.length}
                            </span>
                        </button>
                    )}
                </Grid.Item>
                <Grid.Item span={10}>
                    {team === 2 ? (
                        <div className={`event-row-inner right ${eventMediaList.length > 0 ? 'has-media' : ''}`}>
                            <span className="compact-event-icon-wrap">
                                <img src={iconSrc} alt="icon" className="compact-event-icon" />
                            </span>
                            <span className="compact-time-text">{formatTime(item.eventTime)}</span>
                            <span className="compact-name-text">{item.userName}</span>
                        </div>
                    ) : null}
                </Grid.Item>
            </Grid>
        )
    }

    const renderHappyEventItem = (item) => {
        const eventMediaList = getEventMediaList(item);
        const hasEventVideo = eventMediaList.some(media => getMediaType(media) === 2);
        const previewMediaList = eventMediaList.slice(0, 3);

        return (
            <div key={item.id} className={`home-happy-event-card ${eventMediaList.length > 0 ? 'has-media' : 'no-media'}`}>
                <div className="home-happy-event-head">
                    <div className="home-happy-event-main">
                        <div className="home-happy-event-title">{item.eventInfo || item.eventName || '赛外事件'}</div>
                        <div className="home-happy-event-members">{item.userName || '球队纪念'}</div>
                    </div>
                </div>
                {eventMediaList.length > 0 && (
                    <button
                        type="button"
                        className="home-happy-event-media"
                        onClick={(event) => {
                            // 首页整块最近赛事会跳比赛详情，赛外媒体入口需要阻止冒泡后直接打开影像。
                            event.stopPropagation();
                            recordMediaViews(eventMediaList);
                            setSelectedEventAlbum({ event: item, mediaList: eventMediaList });
                        }}
                    >
                        <span className={`home-happy-event-thumb-grid count-${previewMediaList.length}`}>
                            {previewMediaList.map((media, mediaIndex) => (
                                <span key={media.id || mediaIndex} className="home-happy-event-thumb">
                                    <img src={buildThumbUrl(media)} alt={getMediaType(media) === 2 ? '赛外视频缩略图' : '赛外图片缩略图'} />
                                    {getMediaType(media) === 2 && <i>▶</i>}
                                </span>
                            ))}
                        </span>
                        <em>{hasEventVideo ? '视频/图片' : '图片'} {eventMediaList.length}</em>
                    </button>
                )}
            </div>
        )
    }

    const gameDetailInfomation = () => {
        const startEvent = normalEventInfo?.filter(item => ["8", "10"].includes(String(item.eventId)))
        const endEvent = normalEventInfo?.filter(item => String(item.eventId) === "7")

        const getTeamList = (startTime, endTime, type) => {
            return normalEventInfo?.filter(item => (
                ["11", "12", "13", "14", "15", "16", "3", "4", "5", "6", "19", "20", "21", "9"].includes(String(item.eventId))
                && item.gameTeam === type
                && new Date(item.currentTime) < endTime
                && new Date(item.currentTime) > startTime
            ))
        }

        const getCumulativeScore = (endTime) => {
            const greenScore = normalEventInfo?.filter(item => {
                const time = new Date(item.currentTime);
                const eid = String(item.eventId);
                return time <= endTime && (
                    (isScoreEvent(item.eventId) && item.gameTeam === 1) ||
                    (eid === "4" && item.gameTeam === 2)
                );
            }).length;

            const redScore = normalEventInfo?.filter(item => {
                const time = new Date(item.currentTime);
                const eid = String(item.eventId);
                return time <= endTime && (
                    (isScoreEvent(item.eventId) && item.gameTeam === 2) ||
                    (eid === "4" && item.gameTeam === 1)
                );
            }).length;

            return `${greenScore}:${redScore}`;
        }

        let totalTime = 0

        return (<div className="event-details-section">
            {startEvent?.map((item, index) => {
                const startTimeStr = item?.currentTime;
                const endTimeStr = endEvent[index]?.currentTime;

                const startTime = parseEventDate(startTimeStr);
                const endTime = parseEventDate(endTimeStr);

                const blueTeamList = getTeamList(startTime, endTime, 1);
                const redTeamList = getTeamList(startTime, endTime, 2);

                const mergedSortedList = [...blueTeamList, ...redTeamList].sort((a, b) =>
                    new Date(a.currentTime) - new Date(b.currentTime)
                );

                const wallSeconds = startTime && endTime
                    ? Math.max(Math.floor((endTime - startTime) / 1000), 0)
                    : 0;
                // 单节和总时长统一使用实际比赛时间，扣除本节 eventId=18 的暂停区间。
                const durationSeconds = Math.max(wallSeconds - getPauseSecondsBetween(startTime, endTime), 0);
                const durationMinutes = Math.floor(durationSeconds / 60);

                if (!isNaN(durationSeconds)) {
                    totalTime += durationSeconds
                }

                return (
                    <div key={index} className="event-section-block">
                        <Divider className="game-event-divider">
                            第{index + 1}节 {getCumulativeScore(endTime)} ({durationMinutes}')
                        </Divider>
                        <div className="event-timeline-container">
                            {mergedSortedList.length > 0 ? mergedSortedList.map((eventItem, idx) => (
                                <div key={eventItem.id || idx}>
                                    {renderEventItem(eventItem)}
                                </div>
                            )) : (
                                <div className="game-event-empty-quarter">本节暂无进球、牌罚记录</div>
                            )}
                        </div>
                    </div>
                )
            })}
            <Divider className="game-event-divider total-time-divider">
                总时长: {Math.floor(totalTime / 60)}'
            </Divider>
        </div>)
    }


    return (
        <div className="game-event-rebound">
            <div className="game-event-meta-top">
                <div className="game-event-main-name">{gameInfo?.gameName}</div>
                <div className="game-event-center-info">
                    <div className="meta-item">📅 {gameInfo.startDate} {gameInfo.startTime} ({gameInfo.week})</div>
                    {gameInfo?.address && (
                        <div className="meta-item">📍 {gameInfo.address}</div>
                    )}
                    {gameInfo?.gameRemark && (
                        <div className="meta-item remark">📝 {gameInfo?.gameRemark}</div>
                    )}
                </div>
            </div>

            <Grid columns={3} className="match-score-strip">
                <Grid.Item span={1} className="team-cell">
                    <AutoCenter>
                        {renderTeamLogo(1)}
                    </AutoCenter>
                    <AutoCenter className="team-name-label">{gameInfo?.greenName}</AutoCenter>
                </Grid.Item>
                <Grid.Item span={1} className="score-cell">
                    <AutoCenter className="game-event-score">
                        {gameInfo?.greenGoal}:{gameInfo?.redGoal}
                    </AutoCenter>
                    <AutoCenter>
                        {gameInfo?.status === 3 ? <span className="game-status-label-minimal">已结束</span> : null}
                    </AutoCenter>
                </Grid.Item>
                <Grid.Item span={1} className="team-cell">
                    <AutoCenter>
                        {renderTeamLogo(2)}
                    </AutoCenter>
                    <AutoCenter className="team-name-label">{gameInfo?.redName}</AutoCenter>
                </Grid.Item>
            </Grid>

            {happyEvents.length > 0 && (
                <div className="home-happy-event-list">
                    {happyEvents.map(renderHappyEventItem)}
                </div>
            )}

            {gameDetailInfomation()}

            <Popup
                visible={!!selectedEventAlbum}
                onMaskClick={() => setSelectedEventAlbum(null)}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, background: '#FFFFFF', maxHeight: '86vh', overflowY: 'auto' }}
            >
                {selectedEventAlbum && (
                    <div className="home-event-album-popup">
                        <div className="home-event-album-popup-head">
                            <div>
                                <div className="home-event-album-popup-kicker">MATCH MOMENT</div>
                                <div className="home-event-album-popup-title">{selectedEventAlbum.event?.eventName || selectedEventAlbum.event?.eventInfo || '赛况影像'}</div>
                            </div>
                            <div className="home-event-album-popup-actions">
                                {/* 从事件影像弹窗直接跳到本场相册，保持当前比赛筛选条件。 */}
                                <button
                                    type="button"
                                    className="home-event-album-view-all"
                                    onClick={() => {
                                        setSelectedEventAlbum(null);
                                        navigate('/album', { state: { gameId: gameInfo?.id, gameName: gameInfo?.gameName } });
                                    }}
                                >
                                    本场影像
                                </button>
                                <button type="button" onClick={() => setSelectedEventAlbum(null)}>关闭</button>
                            </div>
                        </div>
                        {selectedEventMediaList.length > 0 ? (
                            <div className="home-event-album-media-list">
                                {selectedEventMediaList.map((media, mediaIndex) => (
                                    <div key={media.id || mediaIndex} className="home-event-album-media-card">
                                        {getMediaType(media) === 2 ? (
                                            <video
                                                className="home-event-album-media"
                                                src={getMediaUrl(media)}
                                                poster={buildThumbUrl(media)}
                                                controls
                                            />
                                        ) : (
                                            <img
                                                className="home-event-album-media"
                                                src={getMediaUrl(media)}
                                                alt={media.remark || '赛况图片'}
                                            />
                                        )}
                                        <div className="home-event-album-media-info">
                                            <span>{getMediaDisplayName(media)}</span>
                                            {(media.tagName || media.tag_name) && <i>{media.tagName || media.tag_name}</i>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty description="暂无影像" imageStyle={{ width: 80 }} />
                        )}
                    </div>
                )}
            </Popup>
        </div>
    )
}

export default GameEventList;
